/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class SlideoutDOM
 * @hideconstructor
 * @classdesc API class that is used to manage Slideout functionality
 * (including the corrsponding view) across API calls
 */
define([
	'react',
	'react-dom',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./InAppSlideoutDOMAPI',
	'./InAppSlideoutContainer',
	'jquery'
], function(React, ReactDOM, APIFactory, InAppSlideoutDOMAPI, InAppSlideoutContainer, $) {

	class InAppSlideoutDOM {
		constructor(options) {
			this._slideout = options.features['InAppSlideoutState'];
			this._api = null;
			this._root = null;
			this._currentView = null;
			this._views = [];
			this._domListeners = [];
			this._features = options.features;
		}

		getAPI() {
			return this._api;
		}

		initialize() {
			this._api = APIFactory.createAPI(this, [InAppSlideoutDOMAPI]);
			this._slideout.onStateChange(this._onSlideoutStateChange.bind(this));
		}

		_onSlideoutStateChange(state = {}) {
			const slideoutState = state.sidePanel;
			if (this._shouldRenderSlideout(state)) {
				this.render();
			} else if (!slideoutState.isOpen && slideoutState.current && this._root) {
				// _onSlideoutStateChange is being called twice, once for DashboardState:setSidePanelOpen with
				// state.sidePanel.isOpen and state.sidePanel.current to allow the panel to be identifiable and close down gracefully
				// The second call occur during the DashboardState.setSidePanelCurrentView(null) to clear the side panel
				// this time state.sidePanel.current is null
				// So check to make sure we have a valid sstate.sidePanel.current
				const { instance: contribution } = this.getView(slideoutState.current);
				if (contribution.close) {
					contribution.close();
				}
				// do animation - slide out, width will become 0px
				const slideOutStyle = {
					'width': '0px'
				};
				return this._doAnimation(slideOutStyle, () => {
					this._unmountContribution();
					this._root && ReactDOM.unmountComponentAtNode(this._root);
					this._dashboardFrameRight && this._root && this._dashboardFrameRight.removeChild(this._root);
					this._root = null;
					this._triggerResizeEvent();
				});
			}
		}

		_doAnimation(animateToStyle, postAnimationCallback) {
			const $_root = $(this._root);
			return $_root.animate(animateToStyle, 'fast', postAnimationCallback);
		}

		_unmountContribution() {
			if (this._currentView && this._currentView.unmount) {
				if (this._currentView.unmount) {
					this._currentView.unmount();
				}
				if (this._currentView.close) {
					this._currentView.close();
				}
			}
			this._currentView = null;
		}

		_triggerResizeEvent() {
			// Required to create the event like this for IE
			const resizeEvent = document.createEvent('HTMLEvents');
			resizeEvent.initEvent('resize', false, true);
			window.dispatchEvent(resizeEvent);
		}

		registerView(id, instance) {
			const view = this.getView(id);
			if (view) {
				throw new Error(`View with id "${id}" is already registered`);
			}
			this._views.push({
				id,
				instance
			});
		}

		removeView(id) {
			const index = this._views.findIndex((item) => item.id === id);
			if (index === -1) {
				throw new Error(`View with id "${id}" does not exist`);
			}
			this._views.splice(index, 1);
		}

		getView(id) {
			return this._views.find(item => item.id === id);
		}

		getViews() {
			return this._views;
		}

		_setupRenderNode(){
			if (!this._root || !this._dashboardFrameRight){
				// TODO: get the canvas node from a feature. for now will do
				// RTC: 295170
				this._dashboardFrameRight = $('.dashboardFrameRight:visible')[0];
				if(this._dashboardFrameRight){
					this._root = document.createElement('div');
					this._root.setAttribute('class', 'dashboardAuthoringToolsPane');

					// set initial width to 0px
					this._root.setAttribute('style', 'width: 0px;');

					// append the inAppSlideOut into dashboardFrameRight
					this._dashboardFrameRight.appendChild(this._root);

					// do animationm - slide in, width will become 320px
					const customPanelWidth = this._features['DashboardSettings'].get('panelWidth');
					const panelWidth = customPanelWidth ? customPanelWidth : '320px';
					const slideInStyle = {
						'width': panelWidth
					};
					this._doAnimation(slideInStyle, () => {
						this._triggerResizeEvent();
					});
				}
			}
		}

		render(options) {
			this._setupRenderNode();
			return new Promise((resolve, reject) => {
				try {
					const { id } = this._slideout.getCurrent();
					const { instance: contribution } = this.getView(id);
					if (contribution) {
						this._currentView = contribution;
						ReactDOM.render(
							<InAppSlideoutContainer
								active={this._slideout.isOpen()}
								contribution={contribution}
								options={options}
								onDomStateChange={this._onUiDomStateChange.bind(this)}
							/>,
							this._root,
							() => resolve()
						);
					} else {
						resolve();
					}
				} catch(error) {
					reject(error);
				}
			});
		}

		destroy() {
			this._root && ReactDOM.unmountComponentAtNode(this._root);
			this._dashboardFrameRight && this._root && this._dashboardFrameRight.removeChild(this._root);
			this._root = null;
			this._api = null;
			this._currentView = null;
			this._domListeners.length = 0;
		}

		onDomStateChange(fn) {
			const newLength = this._domListeners.push(fn);
			const elementIndex = newLength - 1;
			const removeFn = () => {
				this._domListeners.splice(elementIndex, 1);
			};
			return removeFn;
		}

		/**
		 * Invoke registered views when the InAppSlideoutContainer component get mount/unmounted in the DOM
		 *
		 * @param {boolean} state.type - ['mounted' | 'unmounted']
		 * @param {string} [state.current] - the current contribution id
		 */
		_onUiDomStateChange (state) {
			if (!this._domListeners){
				return;
			}
			this._domListeners.forEach(fn => fn(state));
		}

		/**
		 * Returns a boolean indicating whether should render the slide out or not
		 * @param {object} state - the state provided by the DashboardState object
		 * @param {boolean} state.dirty - something changed in the dashboard marking this dashboard as dirty
		 * @param {boolean} state.selectionProperties - flag set by TimelineSliderView.js:openAnimationProperties
		 * @param {boolean} state.sidePanel.isOpen - flag use to open or close the panel
		 *
		 */
		_shouldRenderSlideout (state = {}) {
			const shouldRender = !this._currentView || this._currentView.getProviderId() !== state.sidePanel.current;
			return  state.sidePanel.isOpen && (shouldRender || state.dirty || state.selectionProperties);
		}
	}

	return InAppSlideoutDOM;
});
